import { Searchbar } from '@/components/Searchbar';
import { Button } from '@/lib/Buttons';
import { EDataSourceStatus } from '@/models/dataSource';
import { DateTime } from 'luxon';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuStar } from 'react-icons/lu';
import { twMerge } from 'tailwind-merge';
import { CitizensCount } from '../components/CitizensCount';
import { ComuniList } from '../useComuniLists';

type RecipientListFilterValue = 'all' | 'favorites';

/** Temporary until favorites are mapped from the API. */
type RecipientListItem = ComuniList & { isFavorite?: boolean };

export const CheckSquare: FC<{ checked: boolean }> = ({ checked }) =>
  checked ? (
    <span className='mt-0.5 flex size-5 flex-none items-center justify-center rounded-[5px] bg-brand-700'>
      <LuCheck className='size-3 text-white-100' strokeWidth={3} />
    </span>
  ) : (
    <span className='mt-0.5 size-5 flex-none rounded-[5px] border-[1.5px] border-quaternary-300 bg-white-100' />
  );

const RecipientCard = ({ recipient, isSelected, onToggle }: { recipient: RecipientListItem; isSelected: boolean; onToggle: () => void }) => {
  const { t } = useTranslation('comuni');

  return (
    <button
      type='button'
      onClick={onToggle}
      className={twMerge(
        'relative flex w-full items-start gap-3 rounded-[13px] p-4 text-left',
        isSelected ? 'border-[1.5px] border-brand-600 bg-brand-50' : 'border border-secondary-border bg-white-100 hover:border-brand-200'
      )}
    >
      <CheckSquare checked={isSelected} />
      <span className='min-w-0 flex-1 pr-6'>
        <span className='block truncate text-[14.5px] font-semibold text-primary-900'>{recipient.name}</span>
        <span className='mt-1.5 block text-[12.5px] text-secondary-700'>
          {recipient.status === EDataSourceStatus.ACTIVE ? (
            <>
              <CitizensCount datasourceId={recipient.datasources[0]?.id} /> {t('composer.rows')}
            </>
          ) : (
            t('lists.status.processing')
          )}
        </span>
        <span className='mt-1.5 block text-[11.5px] text-quaternary-400'>
          {t('composer.extractionOf', { date: DateTime.fromISO(recipient.createdOn).toFormat('dd/LL/yyyy') })}
        </span>
      </span>
      {recipient.isFavorite && <LuStar className='absolute right-4 top-4 size-4 flex-none fill-amber-400 text-amber-400' aria-hidden />}
    </button>
  );
};

const RecipientPagination = ({ page, setPage, totalPages }: { page: number; setPage: (page: number) => void; totalPages: number }) => {
  const { t } = useTranslation('comuni');

  return (
    <div className='flex items-center justify-center gap-2'>
      <Button intent='secondary' size='sm' text={t('composer.previous')} onClick={() => setPage(page - 1)} disabled={page === 1} />
      <span>{t('composer.pageOf', { page, total: totalPages })}</span>
      <Button intent='primary' size='sm' text={t('composer.next')} onClick={() => setPage(page + 1)} disabled={page === totalPages} />
    </div>
  );
};

const RecipientListFilter = ({ filter, onFilterChange }: { filter: RecipientListFilterValue; onFilterChange: (filter: RecipientListFilterValue) => void }) => {
  const { t } = useTranslation('comuni');

  const options: { value: RecipientListFilterValue; label: string }[] = [
    { value: 'all', label: t('composer.filterAll') },
    { value: 'favorites', label: t('composer.filterFavorites') },
  ];

  return (
    <div role='tablist' aria-label={t('composer.listFilter')} className='inline-flex overflow-hidden rounded-lg border border-secondary-border'>
      {options.map((option) => {
        const isActive = filter === option.value;

        return (
          <button
            key={option.value}
            type='button'
            role='tab'
            aria-selected={isActive}
            onClick={() => onFilterChange(option.value)}
            className={twMerge(
              'px-4 py-2 text-sm font-semibold transition-colors',
              isActive ? 'bg-brand-700 text-white-100' : 'bg-white-100 text-secondary-700 hover:text-brand-700'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const RecipientSearch = ({ search, setSearch }: { search: string; setSearch: (search: string) => void }) => {
  const { t } = useTranslation('comuni');

  return <Searchbar value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('composer.searchForList')} name='search-recipients' />;
};

/**
 * list is an array of recipients
 * list should searchable and only name matched with the search input will be shown as Card Grid
 * searched list should be paginated and only 6 items per page will be shown
 * @param list - array of recipients
 * @returns
 */
export const RecipientsLists = ({
  list = [],
  selectedIds,
  onToggleList,
}: {
  list: RecipientListItem[];
  selectedIds: string[];
  onToggleList: (id: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RecipientListFilterValue>('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  const filteredList = useMemo(() => {
    const byFavorite = filter === 'favorites' ? list.filter((recipient) => recipient.isFavorite === true) : list;

    if (!search.length) return byFavorite;

    const query = search.toLowerCase();
    return byFavorite.filter((recipient) => recipient.name.toLowerCase().includes(query));
  }, [list, search, filter]);

  const paginatedList = useMemo(() => {
    if (filteredList.length <= 6) return filteredList;
    return filteredList.slice((page - 1) * 6, page * 6);
  }, [filteredList, page]);

  return (
    <div className='grid w-full grid-cols-1 gap-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <RecipientListFilter filter={filter} onFilterChange={setFilter} />
        <RecipientSearch search={search} setSearch={setSearch} />
      </div>
      <div className='grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-3'>
        {paginatedList.map((recipient) => {
          const isSelected = selectedIds.includes(recipient.id);
          return <RecipientCard key={recipient.id} recipient={recipient} isSelected={isSelected} onToggle={() => onToggleList(recipient.id)} />;
        })}
      </div>
      {filteredList.length > 6 && <RecipientPagination page={page} setPage={setPage} totalPages={Math.ceil(filteredList.length / 6)} />}
    </div>
  );
};
